/**
 * SignConfirmSheet — подтверждение подписи отчёта для офисного персонала (§73/2.2).
 *
 * Офисный сотрудник аутентифицирован сессией (email+пароль) и подписывает
 * табель без PIN — доверенный actor (backend §73/1.2 принимает подпись без PIN).
 * Поэтому вместо `PinSheet` (NumericPad + счётчик попыток + lockout) ему
 * показываем лёгкое подтверждение: имя (подпись именная) + одна кнопка
 * «Подписать». Линейный персонал по-прежнему идёт через `PinSheet`.
 *
 * Тот же bottom-`<Sheet>`-базис, что у `PinSheet`/`TabelEditorSheet` — единый
 * визуальный язык worker-PWA (крупная кнопка под палец, condensed uppercase,
 * токены). Пока идёт submit — закрытие свайпом/Esc/крестиком заблокировано
 * (иначе свайп в момент запроса оставит «висящий» fetch).
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export interface SignConfirmSheetProps {
  open: boolean;
  /** Закрытие свайпом / крестиком / Esc. НЕ зовётся при submitting. */
  onOpenChange: (open: boolean) => void;
  /** Имя подписанта — подпись именная (идентичность в момент подписи). */
  name: string;
  /** Идёт POST /sign — блокируем кнопку и закрытие. */
  submitting: boolean;
  /** Подтверждение подписи (без PIN). */
  onConfirm: () => void;
  "data-testid"?: string;
}

export function SignConfirmSheet({
  open,
  onOpenChange,
  name,
  submitting,
  onConfirm,
  "data-testid": testId = "field-sign-confirm",
}: SignConfirmSheetProps): React.ReactElement {
  const { t } = useTranslation();

  const handleOpenChange = (next: boolean): void => {
    if (!next && submitting) return;
    onOpenChange(next);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        showCloseButton={!submitting}
        data-testid={testId}
        className="rounded-t-[2px] p-0"
        onPointerDownOutside={(e) => {
          if (submitting) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (submitting) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (submitting) e.preventDefault();
        }}
      >
        <SheetHeader className="px-5 pt-5 pb-2">
          <SheetTitle
            className="font-condensed text-[16px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: "var(--fg-1)", fontFamily: "var(--font-display)" }}
          >
            {t("field.sign.confirm.title")}
          </SheetTitle>
          <SheetDescription
            className="font-condensed text-[11px] uppercase tracking-[0.18em]"
            style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
          >
            {t("field.sign.confirm.body")}
          </SheetDescription>
        </SheetHeader>

        {name && (
          <div
            className="mx-5 mt-1 mb-1 truncate font-condensed text-[18px] font-semibold uppercase leading-tight tracking-[0.04em]"
            style={{ color: "var(--fg-1)" }}
            data-testid={`${testId}-name`}
          >
            {name}
          </div>
        )}

        <div
          className="mt-3 flex items-stretch px-5 pt-4"
          style={{
            borderTop: "1px solid var(--rule-line)",
            paddingBottom: "max(16px, env(safe-area-inset-bottom, 16px))",
          }}
        >
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="inline-flex h-12 flex-1 items-center justify-center gap-2 px-5 font-condensed text-[14px] font-semibold uppercase tracking-[0.12em] transition-colors disabled:opacity-70"
            style={{
              color: "var(--fg-inverse)",
              background: "var(--soot-900, #16181b)",
              borderRadius: "var(--radius-button)",
              fontFamily: "var(--font-display)",
            }}
            data-testid={`${testId}-submit`}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t("field.sign.confirm.submitting")}
              </>
            ) : (
              <>{t("field.sign.button")} →</>
            )}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
